import React from 'react';
import { motion } from 'framer-motion';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

const useStyles = createUseStyles({
	faq: {
		paddingTop: '6rem',
		paddingBottom: '6rem',
	},
	accordion: {
		'--bs-accordion-bg': 'transparent',
		'--bs-accordion-border-color': 'rgba(0, 0, 0, 0.08)',
		'--bs-accordion-border-radius': '0.75rem',
		'--bs-accordion-active-bg': 'rgba(13, 202, 240, 0.06)',
		'--bs-accordion-active-color': '#0a3a4a',
		'--bs-accordion-btn-focus-box-shadow': 'none',
	},
	item: {
		marginBottom: '1rem',
		overflow: 'hidden',
	},
	question: {
		fontWeight: 600,
		fontSize: '1.125rem',
	},
	answer: {
		fontSize: '1rem',
		color: 'var(--bs-gray-700)',
		lineHeight: 1.65,
	},
});

const FAQS = [
	{
		id: 'what-is',
		question: 'What is ai4us?',
		answer:
			"ai4us is a no-code AI platform that lets anyone build, train, and deploy custom machine-learning models. Bring your data, pick a model recipe, and we handle the rest — from training infrastructure to a live API endpoint.",
	},
	{
		id: 'coding',
		question: 'Do I need to know how to code?',
		answer:
			"No. The whole platform is designed for people who don't write code. If you can fill out a form and upload a file, you can ship an AI model. Developers can still call the resulting models from any app via our REST API or libraries.",
	},
	{
		id: 'data',
		question: 'What kind of data do I need to bring?',
		answer:
			"It depends on the model type. Image models need labeled images, text models need example documents, tabular models need a CSV or spreadsheet. We also include an integrated public-data catalog you can use as a starting point if you don't have your own data yet.",
	},
	{
		id: 'time',
		question: 'How long does it take to train a model?',
		answer:
			'Most models train in minutes to a couple of hours, depending on dataset size and model type. You can start using a trained model immediately — no extra deployment step required.',
	},
	{
		id: 'integrate',
		question: 'How do I use my model in my app?',
		answer:
			"Every model gets a hosted endpoint with a stable URL and an API key. Call it over HTTPS from any language, or use one of our client libraries. You can also download the trained model artifact if you'd rather host it yourself.",
	},
	{
		id: 'cost',
		question: 'How much does it cost?',
		answer:
			'You can build and try your first model for free. After that, pricing scales with usage — pay for the training compute and inference calls you actually use, with no minimums. Reach out to support@ai4us.net if you have questions about volume pricing.',
	},
];

const containerVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const SectionFAQ = () => {
	const classes = useStyles();
	return (
		<div id='faq' className={classNames('row scroll-margin', classes.faq)}>
			<div className='col-12 text-center mb-3'>
				<div className='display-4 fw-bold'>Questions?</div>
			</div>
			<div className='col-12 text-center mb-5'>
				<p className='lead text-muted mb-0'>
					Quick answers to the things people ask before they get started.
				</p>
			</div>
			<div className='col-lg-10 mx-auto'>
				<motion.div
					className={classNames('accordion', classes.accordion)}
					id='faqAccordion'
					variants={containerVariants}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.1 }}>
					{FAQS.map((faq, index) => {
						const headingId = `faq-heading-${faq.id}`;
						const collapseId = `faq-collapse-${faq.id}`;
						const isFirst = index === 0;
						return (
							<motion.div
								key={faq.id}
								className={classNames('accordion-item', classes.item)}
								variants={itemVariants}>
								<h3 className='accordion-header' id={headingId}>
									<button
										className={classNames('accordion-button', classes.question, {
											collapsed: !isFirst,
										})}
										type='button'
										data-bs-toggle='collapse'
										data-bs-target={`#${collapseId}`}
										aria-expanded={isFirst ? 'true' : 'false'}
										aria-controls={collapseId}>
										{faq.question}
									</button>
								</h3>
								<div
									id={collapseId}
									className={classNames('accordion-collapse collapse', {
										show: isFirst,
									})}
									aria-labelledby={headingId}
									data-bs-parent='#faqAccordion'>
									<div className={classNames('accordion-body', classes.answer)}>
										{faq.answer}
									</div>
								</div>
							</motion.div>
						);
					})}
				</motion.div>
			</div>
		</div>
	);
};

export default SectionFAQ;
